import { BaseService, ServiceResponse } from './base.service';
import type { 
  CourseActivity, 
  LocalLegendStatus 
} from '../../types/performance-zones';

export class LocalLegendService extends BaseService {

  // Get user's course activity for the last 90 days
  async getCourseActivity(
    userId: string, 
    days: number = 90,
    courseId?: string
  ): Promise<ServiceResponse<CourseActivity[]>> {
    try {
      const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      
      let query = this.supabase
        .from('course_activity_log')
        .select(`
          *,
          courses!inner(name)
        `)
        .eq('user_id', userId)
        .gte('played_at', cutoffDate);

      if (courseId) {
        query = query.eq('course_id', courseId);
      }

      query = query.order('played_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to include course name
      const transformedData: CourseActivity[] = data?.map(activity => ({
        ...activity,
        course_name: activity.courses?.name
      })) || [];

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get Local Legend status for a user at a specific course
  async getLocalLegendStatus(
    userId: string, 
    courseId: string
  ): Promise<ServiceResponse<LocalLegendStatus | null>> {
    try {
      // Call the database function to get Local Legend
      const { data, error } = await this.supabase
        .rpc('get_local_legend', {
          p_course_id: courseId
        });

      if (error) throw error;

      if (!data || data.length === 0) {
        return this.handleResponse(null); // No Local Legend for this course
      }

      const legendData = data[0];
      
      // Get course name
      const { data: courseData } = await this.supabase
        .from('courses')
        .select('name')
        .eq('id', courseId)
        .single();

      const legendStatus: LocalLegendStatus = {
        user_id: legendData.user_id,
        course_id: courseId,
        round_count: legendData.round_count,
        first_name: legendData.first_name,
        last_name: legendData.last_name,
        nickname: legendData.nickname,
        is_current_legend: legendData.user_id === userId,
        course_name: courseData?.name || 'Unknown Course'
      };

      return this.handleResponse(legendStatus);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get all courses where user is currently Local Legend
  async getUserLocalLegends(userId: string): Promise<ServiceResponse<LocalLegendStatus[]>> {
    try {
      // Get all courses the user has played in the last 90 days
      const { data: userCourses } = await this.supabase
        .from('course_activity_log')
        .select('course_id')
        .eq('user_id', userId)
        .gte('played_at', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString());

      if (!userCourses || userCourses.length === 0) {
        return this.handleResponse([]);
      }

      const uniqueCourseIds = [...new Set(userCourses.map(c => c.course_id))];
      const localLegends: LocalLegendStatus[] = [];

      // Check each course to see if user is Local Legend
      for (const courseId of uniqueCourseIds) {
        const legendResult = await this.getLocalLegendStatus(userId, courseId);
        if (legendResult.success && legendResult.data && legendResult.data.is_current_legend) {
          localLegends.push(legendResult.data);
        }
      }

      return this.handleResponse(localLegends);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Add activity log entry when a round is completed
  async updateActivityLog(
    userId: string, 
    courseId: string, 
    roundId: string,
    playedAt?: string,
    teeType?: string,
    totalScore?: number
  ): Promise<ServiceResponse<CourseActivity>> {
    try {
      const { data, error } = await this.supabase
        .from('course_activity_log')
        .insert({
          user_id: userId,
          course_id: courseId,
          round_id: roundId,
          played_at: playedAt || new Date().toISOString(),
          tee_type: teeType,
          total_score: totalScore
        })
        .select(`
          *,
          courses!inner(name)
        `)
        .single();

      if (error) throw error;

      const transformedData: CourseActivity = {
        ...data,
        course_name: data.courses?.name
      };

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Clean up activity log entries older than specified days (usually 90)
  async cleanupOldActivity(days: number = 90): Promise<ServiceResponse<{ deleted: number }>> {
    try {
      const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      
      const { error, count } = await this.supabase
        .from('course_activity_log')
        .delete({ count: 'exact' })
        .lt('played_at', cutoffDate);

      if (error) throw error;

      return this.handleResponse({ deleted: count || 0 });
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get course activity leaderboard (top players by rounds in last 90 days)
  async getCourseActivityLeaderboard(
    courseId: string,
    days: number = 90,
    limit: number = 10
  ): Promise<ServiceResponse<Array<{
    user_id: string;
    round_count: number;
    user_name: string;
    first_name?: string;
    last_name?: string;
    nickname?: string;
    is_current_legend: boolean;
  }>>> {
    try {
      const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

      // Get activity counts for all users at this course
      const { data, error } = await this.supabase
        .from('course_activity_log')
        .select(`
          user_id,
          profiles!inner(first_name, last_name, nickname, display_name)
        `)
        .eq('course_id', courseId)
        .gte('played_at', cutoffDate);

      if (error) throw error;

      // Count rounds by user
      const userCounts = new Map<string, {
        user_id: string;
        round_count: number;
        first_name?: string;
        last_name?: string;
        nickname?: string;
        display_name?: string;
      }>();

      data?.forEach(activity => {
        const userId = activity.user_id;
        const existing = userCounts.get(userId);

        if (existing) {
          existing.round_count++;
        } else {
          userCounts.set(userId, {
            user_id: userId,
            round_count: 1,
            first_name: activity.profiles?.first_name,
            last_name: activity.profiles?.last_name,
            nickname: activity.profiles?.nickname,
            display_name: activity.profiles?.display_name
          });
        }
      });

      // Sort by round count and take top users
      const sortedUsers = Array.from(userCounts.values())
        .sort((a, b) => b.round_count - a.round_count)
        .slice(0, limit);

      // Format for response
      const leaderboard = sortedUsers.map((user, index) => ({
        user_id: user.user_id,
        round_count: user.round_count,
        user_name: user.display_name ||
                  `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                  'Unknown Player',
        first_name: user.first_name,
        last_name: user.last_name,
        nickname: user.nickname,
        display_name: user.display_name,
        is_current_legend: index === 0 // First place is the Local Legend
      }));

      return this.handleResponse(leaderboard);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get all courses where user has recent activity (potential Local Legend opportunities)
  async getUserActiveCourses(
    userId: string,
    days: number = 90
  ): Promise<ServiceResponse<Array<{
    course_id: string;
    course_name: string;
    user_round_count: number;
    legend_round_count: number;
    is_legend: boolean;
    rounds_to_legend: number;
  }>>> {
    try {
      const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

      // Get all courses the user has played recently
      const { data: userActivity } = await this.supabase
        .from('course_activity_log')
        .select(`
          course_id,
          courses!inner(name)
        `)
        .eq('user_id', userId)
        .gte('played_at', cutoffDate);

      if (!userActivity || userActivity.length === 0) {
        return this.handleResponse([]);
      }

      // Group by course and count user's rounds
      const courseMap = new Map<string, { course_id: string; course_name: string; user_round_count: number }>();
      
      userActivity.forEach(activity => {
        const courseId = activity.course_id;
        const existing = courseMap.get(courseId);
        
        if (existing) {
          existing.user_round_count++;
        } else {
          courseMap.set(courseId, {
            course_id: courseId,
            course_name: activity.courses?.name || 'Unknown Course',
            user_round_count: 1
          });
        }
      });

      // For each course, get the current Local Legend status
      const activeCourses = [];
      
      for (const courseData of courseMap.values()) {
        const legendResult = await this.getCourseActivityLeaderboard(courseData.course_id, days, 1);
        
        const currentLegend = legendResult.data?.[0];
        const legendRoundCount = currentLegend?.round_count || 0;
        const isLegend = currentLegend?.user_id === userId;
        const roundsToLegend = isLegend ? 0 : Math.max(0, legendRoundCount + 1 - courseData.user_round_count);

        activeCourses.push({
          course_id: courseData.course_id,
          course_name: courseData.course_name,
          user_round_count: courseData.user_round_count,
          legend_round_count: legendRoundCount,
          is_legend: isLegend,
          rounds_to_legend: roundsToLegend
        });
      }

      // Sort by closest to becoming legend (or if already legend, by round count)
      activeCourses.sort((a, b) => {
        if (a.is_legend && !b.is_legend) return -1;
        if (!a.is_legend && b.is_legend) return 1;
        if (a.is_legend && b.is_legend) return b.user_round_count - a.user_round_count;
        return a.rounds_to_legend - b.rounds_to_legend;
      });

      return this.handleResponse(activeCourses);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get notification data for when user gains/loses Local Legend status
  async checkLocalLegendChanges(
    userId: string,
    courseId: string,
    roundId: string
  ): Promise<ServiceResponse<{
    gained_legend: boolean;
    lost_legend: boolean;
    previous_legend?: string;
    current_round_count: number;
  }>> {
    try {
      // Get current Local Legend
      const currentLegendResult = await this.getLocalLegendStatus(userId, courseId);
      const currentLegend = currentLegendResult.data;
      
      // Check if user is now the Local Legend
      const gainedLegend = currentLegend?.is_current_legend || false;
      
      // We'd need to store previous state to detect losses
      // For now, just return gained status
      return this.handleResponse({
        gained_legend: gainedLegend,
        lost_legend: false, // Would need historical tracking
        current_round_count: currentLegend?.round_count || 0
      });
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get summary of all Local Legend opportunities for user
  async getLocalLegendOpportunities(
    userId: string
  ): Promise<ServiceResponse<{
    current_legends: LocalLegendStatus[];
    close_opportunities: Array<{
      course_id: string;
      course_name: string;
      rounds_needed: number;
      current_leader: string;
      leader_count: number;
    }>;
    total_legend_count: number;
  }>> {
    try {
      // Get current legends
      const legendsResult = await this.getUserLocalLegends(userId);
      const currentLegends = legendsResult.data || [];

      // Get active courses and opportunities
      const activeCoursesResult = await this.getUserActiveCourses(userId);
      const activeCourses = activeCoursesResult.data || [];

      // Filter for close opportunities (need 3 or fewer rounds)
      const closeOpportunities = activeCourses
        .filter(course => !course.is_legend && course.rounds_to_legend <= 3 && course.rounds_to_legend > 0)
        .map(course => ({
          course_id: course.course_id,
          course_name: course.course_name,
          rounds_needed: course.rounds_to_legend,
          current_leader: 'Current Leader', // Would need to fetch actual name
          leader_count: course.legend_round_count
        }));

      return this.handleResponse({
        current_legends: currentLegends,
        close_opportunities: closeOpportunities,
        total_legend_count: currentLegends.length
      });
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }
}